import { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Icon } from '@atoms/index';
import { Badge } from '@atoms/index';
import { formatCurrency } from '@utils/currency';
import { cn } from '@utils/cn';
import type {
  MenuItem,
  OptionGroup,
  Option,
  SelectedOption,
  ModifierGroup,
} from '@/types';

interface ProductCustomizationModalProps {
  product: MenuItem;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (item: {
    menuItemId: string;
    quantity: number;
    selectedOptions: SelectedOption[];
    specialInstructions?: string;
    removedIngredientIds?: string[];
    menuItem?: MenuItem;
    itemTotal?: number;
  }) => Promise<void> | void;
  addToCartDisabled?: boolean;
}

interface OptionSelection {
  optionGroupId: string;
  optionIds: string[];
}

export const ProductCustomizationModal = ({
  product,
  isOpen,
  onClose,
  onAddToCart,
  addToCartDisabled,
}: ProductCustomizationModalProps) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string[]>>({});
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [removedIngredientIds, setRemovedIngredientIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Initialize default selections for required option groups and modifier groups
  useEffect(() => {
    if (!isOpen) return;

    const defaults: Record<string, string[]> = {};
    
    // Legacy option groups
    if (product.optionGroups) {
      product.optionGroups.forEach((group) => {
        const isRequired = group.isRequired || group.required || false;
        if (isRequired && group.minSelections >= 1) {
          const defaultOption = group.options.find((opt) => opt.isDefault) || group.options[0];
          if (defaultOption && defaultOption.available) {
            defaults[group.id] = [defaultOption.id];
          }
        }
      });
    }

    // New modifier groups
    if (product.modifierGroups) {
      product.modifierGroups.forEach((modifierGroup) => {
        if (!modifierGroup.isActive) return;
        if (modifierGroup.isRequired && modifierGroup.minSelection >= 1) {
          const defaultOption =
            modifierGroup.modifierOptions?.find((opt) => opt.isDefault) ||
            modifierGroup.modifierOptions?.[0];
          if (defaultOption && defaultOption.isActive) {
            defaults[modifierGroup.id] = [defaultOption.id];
          }
        }
      });
    }

    setSelectedOptions(defaults);
    setQuantity(1);
    setSpecialInstructions('');
    setRemovedIngredientIds([]);
    setErrors({});
  }, [product, isOpen]);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    // Focus close button when modal opens
    closeButtonRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Calculate total price
  const totalPrice = useMemo(() => {
    const basePrice = Number(product.price) || 0;
    let modifierTotal = 0;

    // Legacy option groups
    Object.values(selectedOptions).forEach((optionIds) => {
      optionIds.forEach((optionId) => {
        product.optionGroups?.forEach((group) => {
          const option = group.options.find((opt) => opt.id === optionId);
          if (option) {
            const modifier = Number(option.priceModifier) || 0;
            if (!isNaN(modifier)) {
              modifierTotal += modifier;
            }
          }
        });
      });
    });

    // New modifier groups
    if (product.modifierGroups) {
      product.modifierGroups.forEach((modifierGroup) => {
        const selectedModifierOptions = selectedOptions[modifierGroup.id] || [];
        selectedModifierOptions.forEach((optionId) => {
          const option = modifierGroup.modifierOptions?.find(
            (opt) => opt.id === optionId,
          );
          if (option) {
            const adjustment = Number(option.priceAdjustment) || 0;
            if (!isNaN(adjustment)) {
              modifierTotal += adjustment;
            }
          }
        });
      });
    }

    const calculatedTotal = (basePrice + modifierTotal) * quantity;
    return isNaN(calculatedTotal) ? 0 : calculatedTotal;
  }, [product, selectedOptions, quantity]);

  // Validate selections
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Validate legacy option groups
    if (product.optionGroups) {
      product.optionGroups.forEach((group) => {
        const selected = selectedOptions[group.id] || [];
        const isRequired = group.isRequired || group.required || false;
        
        if (isRequired && selected.length < group.minSelections) {
          newErrors[group.id] = `En az ${group.minSelections} seçim yapmalısınız`;
        }
        
        const maxSelections = group.maxSelections;
        if (maxSelections !== null && maxSelections !== undefined && selected.length > maxSelections) {
          newErrors[group.id] = `En fazla ${maxSelections} seçim yapabilirsiniz`;
        }
        
        if (selected.length > 0 && selected.length < group.minSelections) {
          newErrors[group.id] = `En az ${group.minSelections} seçim yapmalısınız`;
        }
      });
    }

    // Validate new modifier groups
    if (product.modifierGroups) {
      product.modifierGroups.forEach((modifierGroup) => {
        if (!modifierGroup.isActive) return;
        
        const selected = selectedOptions[modifierGroup.id] || [];
        
        if (modifierGroup.isRequired && selected.length < modifierGroup.minSelection) {
          newErrors[modifierGroup.id] = `En az ${modifierGroup.minSelection} seçim yapmalısınız`;
        }
        
        if (modifierGroup.maxSelection !== null && selected.length > modifierGroup.maxSelection) {
          newErrors[modifierGroup.id] = `En fazla ${modifierGroup.maxSelection} seçim yapabilirsiniz`;
        }
        
        if (selected.length > 0 && selected.length < modifierGroup.minSelection) {
          newErrors[modifierGroup.id] = `En az ${modifierGroup.minSelection} seçim yapmalısınız`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle option selection (radio button style for single select)
  const handleOptionSelect = (groupId: string, optionId: string, isMulti: boolean) => {
    setSelectedOptions((prev) => {
      const current = prev[groupId] || [];
      
      if (isMulti) {
        // Multi-select: toggle option
        if (current.includes(optionId)) {
          return { ...prev, [groupId]: current.filter((id) => id !== optionId) };
        } else {
          return { ...prev, [groupId]: [...current, optionId] };
        }
      } else {
        // Single-select: replace selection
        return { ...prev, [groupId]: [optionId] };
      }
    });
    // Clear error for this group
    if (errors[groupId]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
    }
  };

  const handleAddToCart = async () => {
    if (!validate()) {
      // Scroll to first error
      const firstErrorId = Object.keys(errors)[0];
      if (firstErrorId) {
        const errorElement =
          document.getElementById(`option-group-${firstErrorId}`) ||
          document.getElementById(`modifier-group-${firstErrorId}`);
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsAddingToCart(true);

    try {
      // Convert to SelectedOption format
      // Sort to ensure consistent order for cart item key generation
      const selectedOptionsArray: SelectedOption[] = [];
      Object.entries(selectedOptions)
        .sort(([a], [b]) => a.localeCompare(b)) // Sort by groupId for consistency
        .forEach(([groupId, optionIds]) => {
          optionIds.forEach((optionId) => {
            selectedOptionsArray.push({
              optionGroupId: groupId,
              optionId,
            });
          });
        });

      // Calculate per-unit price (without quantity multiplier)
      const basePrice = Number(product.price) || 0;
      let optionsTotal = 0;

      // Legacy option groups
      Object.values(selectedOptions).forEach((optionIds) => {
        optionIds.forEach((optionId) => {
          product.optionGroups?.forEach((group) => {
            const option = group.options.find((opt) => opt.id === optionId);
            if (option) {
              const priceModifier = Number(option.priceModifier) || 0;
              if (!isNaN(priceModifier)) {
                optionsTotal += priceModifier;
              }
            }
          });
        });
      });

      // New modifier groups
      if (product.modifierGroups) {
        product.modifierGroups.forEach((modifierGroup) => {
          const selectedModifierOptions = selectedOptions[modifierGroup.id] || [];
          selectedModifierOptions.forEach((optionId) => {
            const option = modifierGroup.modifierOptions?.find(
              (opt) => opt.id === optionId,
            );
            if (option) {
              const adjustment = Number(option.priceAdjustment) || 0;
              if (!isNaN(adjustment)) {
                optionsTotal += adjustment;
              }
            }
          });
        });
      }

      const unitPrice = basePrice + optionsTotal;

      await onAddToCart({
        menuItemId: product.id,
        quantity,
        selectedOptions: selectedOptionsArray,
        specialInstructions: specialInstructions.trim() || undefined,
        removedIngredientIds: removedIngredientIds.length > 0 ? removedIngredientIds : undefined,
        menuItem: product,
        itemTotal: unitPrice,
      });

      // Small delay for visual feedback before closing
      await new Promise((resolve) => setTimeout(resolve, 150));
      onClose();
    } catch (err) {
      // Error is handled by parent component (RestaurantPage)
      // Don't close modal on error so user can retry
      console.error('Failed to add item to cart:', err);
    } finally {
      setIsAddingToCart(false);
    }
  };

  if (!isOpen) return null;

  const optionGroups = product.optionGroups || [];
  const modifierGroups = product.modifierGroups || [];
  const ingredients = product.ingredients || [];

  const toggleRemovedIngredient = (ingredientId: string) => {
    setRemovedIngredientIds((prev) =>
      prev.includes(ingredientId)
        ? prev.filter((id) => id !== ingredientId)
        : [...prev, ingredientId],
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-4 p-4 sm:p-5 border-b border-gray-100 flex-shrink-0 bg-gray-50/50">
            {product.imageUrl && (
              <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-white border border-gray-100 shadow-sm">
                <img
                  src={product.imageUrl}
                  alt=""
                  className="w-full h-full object-contain object-center"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 id="modal-title" className="text-lg sm:text-xl font-bold text-gray-900 mb-0.5 leading-tight">
                {product.name}
              </h2>
              {product.description && (
                <p id="modal-description" className="text-gray-600 text-sm line-clamp-2 mt-1">
                  {product.description}
                </p>
              )}
              <p className="text-primary font-bold text-base mt-2">
                {formatCurrency(Number(product.price) || 0)}
              </p>
            </div>
            <button
              ref={closeButtonRef}
              onClick={onClose}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label="Kapat"
            >
              <Icon name="close" size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-5 min-h-0">
            {/* Option Groups */}
            {optionGroups.map((group) => {
              const selected = selectedOptions[group.id] || [];
              const maxSelections = group.maxSelections;
              const isMulti = maxSelections === null || maxSelections === undefined || maxSelections > 1;
              const hasError = !!errors[group.id];

              // Check if max selection limit is reached
              const isMaxReached = maxSelections !== null && maxSelections !== undefined && selected.length >= maxSelections;

              return (
                <div key={group.id} id={`option-group-${group.id}`} className="space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{group.name}</h3>
                    {(group.isRequired || group.required) && (
                      <Badge variant="primary" size="sm">
                        Zorunlu
                      </Badge>
                    )}
                    <span className="text-sm text-gray-500">
                      {group.minSelections > 0
                        ? maxSelections === null
                          ? `En az ${group.minSelections} seçim`
                          : group.minSelections === maxSelections
                          ? `${group.minSelections} seçim`
                          : `${group.minSelections}-${maxSelections} seçim`
                        : maxSelections !== null
                        ? `En fazla ${maxSelections} seçim`
                        : 'İsteğe bağlı'}
                    </span>
                    {isMaxReached && (
                      <span className="text-xs text-amber-600 font-medium">(Maksimum seçim limitine ulaştınız)</span>
                    )}
                  </div>
                  
                  {group.description && (
                    <p className="text-sm text-gray-600">{group.description}</p>
                  )}

                  {hasError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                      <p className="text-sm text-red-700 font-medium">{errors[group.id]}</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    {group.options
                      .filter((opt) => opt.available)
                      .map((option) => {
                        const isSelected = selected.includes(option.id);
                        const priceModifier = option.priceModifier || 0;
                        const isDisabled = isMulti && isMaxReached && !isSelected;

                        return (
                          <label
                            key={option.id}
                            className={cn(
                              'flex items-center gap-3 p-3 rounded-lg border-2 transition-all',
                              isDisabled
                                ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                                : isSelected
                                ? 'border-primary bg-primary/5 cursor-pointer shadow-sm'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer',
                              hasError && !isSelected && !isDisabled && 'border-red-200'
                            )}
                          >
                            {isMulti ? (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={() => !isDisabled && handleOptionSelect(group.id, option.id, true)}
                                className={cn(
                                  'w-5 h-5 text-primary rounded border-gray-300 focus:ring-primary focus:ring-2',
                                  isDisabled && 'cursor-not-allowed'
                                )}
                              />
                            ) : (
                              <input
                                type="radio"
                                name={`group-${group.id}`}
                                checked={isSelected}
                                onChange={() => handleOptionSelect(group.id, option.id, false)}
                                className="w-5 h-5 text-primary border-gray-300 focus:ring-primary focus:ring-2"
                              />
                            )}
                            <div className="flex-1">
                              <div className={cn(
                                'font-medium',
                                isDisabled ? 'text-gray-400' : 'text-gray-900'
                              )}>
                                {option.name}
                              </div>
                              {option.description && (
                                <div className={cn(
                                  'text-sm',
                                  isDisabled ? 'text-gray-400' : 'text-gray-500'
                                )}>
                                  {option.description}
                                </div>
                              )}
                            </div>
                            {priceModifier !== 0 && !isNaN(Number(priceModifier)) && (
                              <div className={cn(
                                'text-sm font-semibold',
                                isDisabled ? 'text-gray-400' : priceModifier > 0 ? 'text-green-600' : 'text-red-600'
                              )}>
                                {priceModifier > 0 ? '+' : ''}
                                {formatCurrency(Number(priceModifier) || 0)}
                              </div>
                            )}
                            {(priceModifier === 0 || isNaN(Number(priceModifier))) && (
                              <div className={cn(
                                'text-xs font-medium',
                                isDisabled ? 'text-gray-400' : 'text-gray-500'
                              )}>
                                {formatCurrency(0)}
                              </div>
                            )}
                          </label>
                        );
                      })}
                  </div>
                  </div>
                );
              })}

            {/* Modifier Groups (New Advanced System) */}
            {modifierGroups
              .filter((group) => group.isActive)
              .map((group) => {
                const selected = selectedOptions[group.id] || [];
                const maxSelections = group.maxSelection;
                const isMulti = maxSelections === null || maxSelections > 1;
                const hasError = !!errors[group.id];
                const isMaxReached =
                  maxSelections !== null && selected.length >= maxSelections;

                return (
                  <div
                    key={group.id}
                    id={`modifier-group-${group.id}`}
                    className="space-y-3"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{group.name}</h3>
                      {group.isRequired && (
                        <Badge variant="primary" size="sm">
                          Zorunlu
                        </Badge>
                      )}
                      <span className="text-sm text-gray-500">
                        {group.minSelection > 0
                          ? maxSelections === null
                            ? `En az ${group.minSelection} seçim`
                            : group.minSelection === maxSelections
                            ? `${group.minSelection} seçim`
                            : `${group.minSelection}-${maxSelections} seçim`
                          : maxSelections !== null
                          ? `En fazla ${maxSelections} seçim`
                          : 'İsteğe bağlı'}
                      </span>
                      {isMaxReached && (
                        <span className="text-xs text-amber-600 font-medium">
                          (Maksimum seçim limitine ulaştınız)
                        </span>
                      )}
                    </div>

                    {group.description && (
                      <p className="text-sm text-gray-600">{group.description}</p>
                    )}

                    {hasError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                        <p className="text-sm text-red-700 font-medium">
                          {errors[group.id]}
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      {group.modifierOptions
                        ?.filter((opt) => opt.isActive)
                        .map((option) => {
                          const isSelected = selected.includes(option.id);
                          const priceAdjustment = option.priceAdjustment || 0;
                          const isDisabled = isMulti && isMaxReached && !isSelected;

                          return (
                            <label
                              key={option.id}
                              className={cn(
                                'flex items-center gap-3 p-3 rounded-lg border-2 transition-all',
                                isDisabled
                                  ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                                  : isSelected
                                  ? 'border-primary bg-primary/5 cursor-pointer shadow-sm'
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer',
                                hasError &&
                                  !isSelected &&
                                  !isDisabled &&
                                  'border-red-200',
                              )}
                            >
                              {isMulti ? (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={isDisabled}
                                  onChange={() =>
                                    !isDisabled &&
                                    handleOptionSelect(group.id, option.id, true)
                                  }
                                  className={cn(
                                    'w-5 h-5 text-primary rounded border-gray-300 focus:ring-primary focus:ring-2',
                                    isDisabled && 'cursor-not-allowed',
                                  )}
                                />
                              ) : (
                                <input
                                  type="radio"
                                  name={`modifier-group-${group.id}`}
                                  checked={isSelected}
                                  onChange={() =>
                                    handleOptionSelect(group.id, option.id, false)
                                  }
                                  className="w-5 h-5 text-primary border-gray-300 focus:ring-primary focus:ring-2"
                                />
                              )}
                              <div className="flex-1">
                                <div
                                  className={cn(
                                    'font-medium',
                                    isDisabled ? 'text-gray-400' : 'text-gray-900',
                                  )}
                                >
                                  {option.name}
                                </div>
                                {option.description && (
                                  <div
                                    className={cn(
                                      'text-sm',
                                      isDisabled ? 'text-gray-400' : 'text-gray-500',
                                    )}
                                  >
                                    {option.description}
                                  </div>
                                )}
                                {option.calories && (
                                  <div
                                    className={cn(
                                      'text-xs',
                                      isDisabled ? 'text-gray-400' : 'text-gray-400',
                                    )}
                                  >
                                    {option.calories} kcal
                                  </div>
                                )}
                              </div>
                              {priceAdjustment !== 0 &&
                                !isNaN(Number(priceAdjustment)) && (
                                  <div
                                    className={cn(
                                      'text-sm font-semibold',
                                      isDisabled
                                        ? 'text-gray-400'
                                        : priceAdjustment > 0
                                        ? 'text-green-600'
                                        : 'text-red-600',
                                    )}
                                  >
                                    {priceAdjustment > 0 ? '+' : ''}
                                    {formatCurrency(Number(priceAdjustment) || 0)}
                                  </div>
                                )}
                              {(priceAdjustment === 0 ||
                                isNaN(Number(priceAdjustment))) && (
                                <div
                                  className={cn(
                                    'text-xs font-medium',
                                    isDisabled ? 'text-gray-400' : 'text-gray-500',
                                  )}
                                >
                                  {formatCurrency(0)}
                                </div>
                              )}
                            </label>
                          );
                        })}
                    </div>
                  </div>
                );
              })}

            {ingredients.length > 0 && (
              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold text-gray-900">Çıkarılacak Malzemeler</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    İstemediğiniz malzemeleri işaretleyin
                  </p>
                </div>
                <div className="space-y-2">
                  {ingredients.map((ingredient) => {
                    const isSelected = removedIngredientIds.includes(ingredient.id);
                    return (
                      <label
                        key={ingredient.id}
                        className={cn(
                          'flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors',
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 hover:border-gray-300',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRemovedIngredient(ingredient.id)}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm font-medium text-gray-900">{ingredient.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Special Instructions */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-900">
                Özel istek (isteğe bağlı)
              </label>
              <textarea
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                placeholder="Örn: Az acılı, ekstra sos..."
                className="w-full p-3 text-sm border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                rows={2}
                maxLength={500}
              />
              <p className="text-xs text-gray-400">{specialInstructions.length}/500</p>
            </div>
          </div>

          <div className="border-t border-gray-100 p-4 sm:p-5 bg-white flex-shrink-0 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
            <div className="flex items-center justify-between gap-4 mb-3">
              {/* Quantity Selector */}
              <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 p-1">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  className={cn(
                    'w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
                    quantity <= 1
                      ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900 active:bg-gray-300'
                  )}
                  aria-label="Azalt"
                >
                  <Icon name="minus" size={18} />
                </button>
                <span className="font-semibold text-gray-900 w-8 text-center text-lg">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900 active:bg-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                  aria-label="Artır"
                >
                  <Icon name="plus" size={18} />
                </button>
              </div>

              {/* Total Price */}
              <div className="text-right">
                <div className="text-sm text-gray-500 mb-1">Toplam</div>
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(totalPrice)}
                </div>
              </div>
            </div>

            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={handleAddToCart}
              disabled={isAddingToCart || addToCartDisabled}
              title={addToCartDisabled ? 'Restoran şu an kapalı' : undefined}
              className={cn(
                'rounded-xl font-semibold shadow-lg transition-all',
                isAddingToCart && 'opacity-75 cursor-wait'
              )}
            >
              {isAddingToCart ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Ekleniyor...
                </span>
              ) : addToCartDisabled ? (
                'Restoran kapalı'
              ) : (
                'Sepete Ekle'
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
